import React, { useEffect, useMemo, useRef, useState } from "react";
import "./uploadSingleBatchUserForm.css";
import FormikButtonSelect from "../../../../components/formik/ButtonSelect";
import { Field, Form, Formik } from "formik";
import {
  FieldSpy,
  Input,
  LabeledDatePicker,
  Select,
} from "../../../../components/formik";
import { ButtonWithSpinner } from "../../../../components/common";
import { generatedValidationsForDynamicFields } from "../../validation-schemas/scoreCardCalculate";
import { withError } from "../../../../components/hocs";
import { useDispatch, useSelector } from "react-redux";
import { selectScoreCardCalculateSubmitSuccess } from "../../selectors";
import { clearScoreCardCalculateActionsSubmittedState } from "../../actions/scoreCardCalculateActions";
import {
  DATE_WITH_CURRENT_YEAR,
  DATE_WITH_MIN_BIRTH_YEAR,
} from "../../../../utilities/common";
import useRoles from "../../../../components/hooks/useRoles";
import FormikAsyncSelect from "./AsyncSelect/AsyncSelect";
import { clearOfferIds } from "../../actions/getOfferIdsAction";
import { CLIENT_TYPE } from "../../../reports/constants/singleClientEntriesReportConstants";
import ReactTooltip from "react-tooltip";

const ErrorInput = withError(Input);
const ErrorSelect = withError(Select);
const ErrorAsyncSelect = withError(FormikAsyncSelect);

export const FACTOR_TYPES = {
  STRING: "STRING",
  NUMERIC: "NUMERIC",
  OPTION: "OPTION",
  DATE: "DATE",
};

const widthOrder = { NORMAL: 0, MEDIUM: 1 };

const UploadSingleBatchUserForm = ({
  clientTypes,
  handleSubmit,
  scoreCardsRiskFactors,
  isLoading,
}) => {
  const dispatch = useDispatch();
  const formikRef = useRef(null);
  const { isCustomerSupport } = useRoles();
  const [targetType, setTargetType] = useState(null);
  const [targetTypeCategory, setTargetTypeCategory] = useState(null);
  const [validationSchema, setValidationSchema] = useState(
    generatedValidationsForDynamicFields({}),
  );
  const isSubmitted = useSelector(selectScoreCardCalculateSubmitSuccess);
  const [initialValues, setInitialValues] = useState({});

  useEffect(() => {
    setDefaultClientType();
  }, [clientTypes]);

  useEffect(() => {
    if (isSubmitted) {
      clearFields();
      dispatch(clearScoreCardCalculateActionsSubmittedState());
    }
  }, [isSubmitted]);

  const handleClientTypeChange = (clientType) => {
    formikRef.current.resetForm({
      type: clientType,
      initialValues,
    });

    const currentCategory = clientTypes.find(
      (type) => type.value === clientType,
    ).targetTypeCategory;

    dispatch(clearOfferIds());
    setTargetTypeCategory(currentCategory);
    setTargetType(clientType);
  };

  function setDefaultClientType() {
    if (clientTypes && scoreCardsRiskFactors) {
      const tempTypes = [...clientTypes].sort((a, b) =>
        b.value.localeCompare(a.value),
      );
      const currentCategory = clientTypes.find(
        (type) => type.value === tempTypes[0].value,
      ).targetTypeCategory;

      setTargetTypeCategory(currentCategory);
      setTargetType(tempTypes[0].value);
      formikRef.current.setFieldValue("type", tempTypes[0].value);
    }
  }

  function clearFields() {
    const currentType =
      formikRef.current && formikRef.current.state.values.type;
    formikRef.current.resetForm({
      type: currentType,
      ...initialValues,
    });
    formikRef.current.setFieldValue("type", currentType);
    setTargetType(currentType);
  }

  const riskFactors = useMemo(() => {
    if (targetType && scoreCardsRiskFactors.length) {
      const factor = scoreCardsRiskFactors.find(
        (f) => f.targetType === targetType,
      );

      if (factor) {
        return factor.scoreCardRiskFactors.sort(
          (a, b) => widthOrder[b.width] + 100 - (widthOrder[a.width] + 100),
        );
      }

      return [];
    }
    return [];
  }, [targetType, scoreCardsRiskFactors]);

  useEffect(() => {
    if (riskFactors.length) {
      setValidationSchema(
        generatedValidationsForDynamicFields(riskFactors, targetTypeCategory),
      );
      const values = {};
      riskFactors.forEach((factor) => (values[factor.name] = ""));
      setInitialValues({
        type: targetType,
        clientParams: values,
        companyOfferId: null,
      });
    }
  }, [riskFactors]);

  return (
    <div>
      <Formik
        ref={formikRef}
        enableReinitialize
        validateOnBlur={false}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({ errors, touched }) => {
          return (
            <Form className="upload-single-batch-user-container">
              <div className="upload-single-batch-user-sections">
                <section className="base-fields-section">
                  <div className="p-0 ">
                    <Field
                      type="text"
                      name="type"
                      className="dropdown-group"
                      placeholder="Client Type"
                      component={FormikButtonSelect}
                      options={clientTypes}
                      isClearable
                    />

                    <FieldSpy name="type" onChange={handleClientTypeChange} />
                  </div>
                  {CLIENT_TYPE.PERSON === targetTypeCategory ? (
                    <Field
                      type="text"
                      component={ErrorAsyncSelect}
                      placeholder={"Company Name/OfferID (start typing)"}
                      name={"companyOfferId"}
                      disabled={isCustomerSupport}
                      isClearable
                    />
                  ) : null}
                </section>
                <section className="dynamic-fields-section">
                  {riskFactors && Object.keys(validationSchema).length
                    ? riskFactors.map((factor, i) => (
                        <RenderFieldByType
                          key={i}
                          factor={factor}
                          errors={errors}
                          touched={touched}
                        />
                      ))
                    : null}
                </section>
              </div>
              <div className="d-flex justify-content-center">
                <ButtonWithSpinner
                  text="Submit"
                  disabled={isCustomerSupport}
                  type="submit"
                  className="save-button"
                  loading={isLoading}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );

  function RenderFieldByType({ factor, errors, touched }) {
    const { type, name, scoreCardRiskFactorScores, width, description, hint } =
      factor;
    const isMediumClassName = width === "MEDIUM" ? "medium-size" : "";

    function generateOptions(riskFactorsOptions) {
      return riskFactorsOptions.map((opt) => ({
        label: opt.value,
        value: opt.value,
      }));
    }

    function preventNegativeInput(input, name) {
      if (input < 0) {
        formikRef.current.setFieldValue(`clientParams.${name}`, 1);
      }
    }

    const RenderErrorDate = ({ name }) => {
      if (!name) return null;
      if (!errors.clientParams) return null;
      if (!touched.clientParams) return null;
      if (!touched.clientParams[name]) return null;

      return (
        <p className="form-error-message">
          {errors.clientParams ? errors["clientParams"][name] : null}
        </p>
      );
    };

    switch (type) {
      case FACTOR_TYPES.STRING:
        return (
          <div className={isMediumClassName}>
            <a data-tip={hint}>
              <Field
                type="text"
                component={ErrorInput}
                placeholder={description}
                name={`clientParams.${name}`}
                disabled={isCustomerSupport}
              />
            </a>
            <ReactTooltip place="top" type="info" effect="solid" />
          </div>
        );
      case FACTOR_TYPES.NUMERIC:
        return (
          <div className={isMediumClassName}>
            <a data-tip={hint}>
              <Field
                type="number"
                component={ErrorInput}
                placeholder={description}
                valid={false}
                name={`clientParams.${name}`}
                disabled={isCustomerSupport}
              />
            </a>
            <ReactTooltip place="top" type="info" effect="solid" />

            <FieldSpy
              name={`clientParams.${name}`}
              onChange={(int) => preventNegativeInput(int, name)}
            />
          </div>
        );

      case FACTOR_TYPES.DATE:
        return (
          <div className={isMediumClassName}>
            <a data-tip={hint}>
              <LabeledDatePicker
                dateFormat="dd.MM.yyyy"
                name={`clientParams.${name}`}
                placeholderText={name}
                minDate={DATE_WITH_MIN_BIRTH_YEAR}
                maxDate={DATE_WITH_CURRENT_YEAR}
                yearDropdownItemNumber={120}
                disabled={isCustomerSupport}
                changeOnInput
                label=""
              />
            </a>
            <RenderErrorDate name={name} />
          </div>
        );

      case FACTOR_TYPES.OPTION:
        const options = generateOptions(scoreCardRiskFactorScores);
        return (
          <React.Fragment>
            <div className={isMediumClassName}>
              <Field
                type=""
                component={ErrorSelect}
                placeholder={<a data-tip={hint}>{description}</a>}
                isDisabled={isCustomerSupport}
                name={`clientParams.${name}`}
                className="dropdown-group"
                options={options}
                isClearable
              />
              <ReactTooltip place="top" type="info" effect="solid" />
            </div>
          </React.Fragment>
        );

      default:
        return <p>{type}</p>;
    }
  }
};

export default UploadSingleBatchUserForm;



// WEBPACK FOOTER //
// ./src/features/score-card/components/upload-single-batch-user-form/UploadSingleBatchUserForm.jsx